import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DownloadButton,
  EmptyContent,
  FileFormatIcon,
  Flex,
  Text,
} from '@/components/ui';
import { DataTable } from '@/components/data-table';
import { BaseFile, BaseFileVersion, FileVersionsModalProps } from './types';
import { useCsvApproval } from '@/hooks/useCsvApproval';
import { CsvFileValidationStatus, CsvFileVersion } from '@/types/csvFiles';

import { IconClock, IconX } from '@tabler/icons-react';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Configure dayjs
dayjs.extend(relativeTime);

/* eslint-disable @typescript-eslint/no-explicit-any -- Generic modal supports multiple file/version shapes; strict typing will be improved incrementally. */

/**
 * Generic File Versions Modal Component
 * Displays version history for any file type that implements the BaseFile and BaseFileVersion interfaces
 */
export const FileVersionsModal = <
  TFile extends BaseFile,
  TVersion extends BaseFileVersion,
>({
  file,
  open,
  onOpenChange,
  useVersionsHook,
  getDownloadEndpoint,
  modalTitle = 'Version History',
  modalSize = 'medium',
  showExtensionColumn = true,
}: FileVersionsModalProps<TFile, TVersion>) => {
  const fileId = file?.id ?? 0;
  const {
    data: versions,
    isLoading,
    error,
  } = useVersionsHook(fileId, open && !!file);
  const { updateVersionStatus } = useCsvApproval();

  // Handle different response formats (direct array or wrapped in data property)
  const versionsData = useMemo(() => {
    return Array.isArray(versions) ? versions : versions?.data || [];
  }, [versions]);

  // Find the current version (the one with the latest created_at) - independent of table sorting
  const currentVersionId = useMemo(() => {
    if (versionsData.length === 0) return null;

    const latestVersion = versionsData.reduce((latest, current) => {
      const latestDate = new Date(latest.created_at).getTime();
      const currentDate = new Date(current.created_at).getTime();
      return currentDate > latestDate ? current : latest;
    });

    return latestVersion?.id || null;
  }, [versionsData]);

  // Find the latest imported version (the one with the newest imported_at)
  const latestImportedVersionId = useMemo(() => {
    const importedVersions = (versionsData as any[]).filter(v => !!v?.imported_at);
    if (importedVersions.length === 0) return null;

    const latestImported = importedVersions.reduce((latest, current) => {
      const latestDate = new Date(latest.imported_at as string).getTime();
      const currentDate = new Date(current.imported_at as string).getTime();
      return currentDate > latestDate ? current : latest;
    });

    return latestImported?.id || null;
  }, [versionsData]);

  // Sorting state: sort by created_at descending (newest first) by default
  const [sortingState, setSortingState] = useState<SortingState>([
    { id: 'created_at', desc: true },
  ]);

  const getFileExtension = (filename: string): string => {
    const lastDot = filename.lastIndexOf('.');
    return lastDot > 0 ? filename.substring(lastDot) : '';
  };

  // Check if this is a CSV file version (has version_number, imported_at, imported_by, validation_status)
  const isCsvFileVersion = useMemo(() => {
    if (versionsData.length === 0) return false;
    const firstVersion = versionsData[0] as any;
    return (
      'version_number' in firstVersion ||
      'imported_at' in firstVersion ||
      'imported_by' in firstVersion ||
      'validation_status' in firstVersion
    );
  }, [versionsData]);

  // Handle version status change for CSV files
  const handleVersionStatusChange = useCallback(
    async (version: TVersion, newStatus: CsvFileValidationStatus) => {
      if (!isCsvFileVersion || !fileId) return;

      const csvVersion = version as unknown as CsvFileVersion;
      try {
        // Mutation hook automatically invalidates cache and updates UI
        await updateVersionStatus(fileId, csvVersion, newStatus);
      } catch (error) {
        // Error handling (including toast) is done in mutation hook
        console.error('Failed to update version status:', error);
      }
    },
    [fileId, isCsvFileVersion, updateVersionStatus]
  );

  const columns: ColumnDef<TVersion>[] = useMemo(
    () => [
      {
        id: 'filename',
        accessorFn: (row: TVersion) => row.filename,
        header: 'File Name',
        meta: {
          cell: {
            variant: 'fileName',
            showIcon: true,
            endSlot: (row: TVersion) => {
              const badges = [];

              // Add version number badge for CSV files
              if (isCsvFileVersion && (row as any).version_number) {
                badges.push(
                  <Badge
                    key="version"
                    color="feature"
                    variant="surface"
                    size="xsmall"
                  >
                    v.{(row as any).version_number}
                  </Badge>
                );
              } else if (isCsvFileVersion) {
                badges.push(
                  <Badge
                    key="version-id"
                    color="feature"
                    variant="surface"
                    size="xsmall"
                  >
                    v-id: {row.id}
                  </Badge>
                );
              }

              if (row.id === currentVersionId) {
                badges.push(
                  <Badge
                    key="current"
                    color="info"
                    variant="surface"
                    size="xsmall"
                  >
                    Last version
                  </Badge>
                );
              }

              if (row.id === latestImportedVersionId) {
                badges.push(
                  <Badge
                    key="latest-imported"
                    color="success"
                    variant="surface"
                    size="xsmall"
                  >
                    Current
                  </Badge>
                );
              }

              return badges.length > 0 ? <>{badges}</> : null;
            },
          },
          size: '1fr',
          minSize: 200,
          maxSize: '2fr',
        },
      },
      ...(showExtensionColumn
        ? [
            {
              id: 'extension',
              accessorFn: (row: TVersion) => {
                const extension = getFileExtension(row.filename);
                return extension ? extension.substring(1) : ''; // Remove dot
              },
              header: 'Extension',
              meta: {
                cell: {
                  variant: 'fileFormat',
                },
                minSize: 100,
                maxSize: 130,
              },
              enableSorting: false,
            },
          ]
        : []),
      {
        accessorKey: 'size',
        header: 'Size',
        meta: {
          cell: {
            variant: 'fileSize',
          },
          minSize: 100,
          maxSize: 130,
        },
      },
      {
        accessorKey: 'content_type',
        header: 'Type',
        meta: {
          cell: {
            variant: 'mimeType',
          },
          minSize: 100,
          maxSize: 130,
          defaultVisible: false,
        },
      },
      {
        accessorKey: 'created_by',
        header: 'Uploaded by',
        meta: {
          cell: {
            variant: 'user',
            showEmail: true,
          },
          minSize: 200,
          maxSize: 230,
        },
        enableSorting: false,
      },
      {
        accessorKey: 'created_at',
        header: 'Uploaded',
        meta: {
          cell: {
            variant: 'datetime',
            relative: true,
          },
          minSize: 200,
          maxSize: 230,
        },
      },
      // CSV-specific columns
      ...(isCsvFileVersion
        ? [
            {
              accessorKey: 'validation_status',
              header: 'Validation Status',
              meta: {
                cell: {
                  variant: 'validationStatus',
                  onStatusChange: (
                    file: any,
                    status: CsvFileValidationStatus
                  ) => {
                    handleVersionStatusChange(file as TVersion, status);
                  },
                },
                size: '180px',
                minSize: 150,
                maxSize: 220,
              },
              enableSorting: false,
            },
            {
              id: 'imported_version_info',
              accessorFn: (row: TVersion) => row,
              header: 'Imported Version',
              meta: {
                cell: {
                  variant: 'versionImportInfo',
                },
                size: '180px',
                minSize: 150,
                maxSize: 220,
              },
              enableSorting: false,
            },
          ]
        : []),
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => {
          // Ensure file and file.id exist before rendering download button
          if (!fileId || !row.original.id) {
            return (
              <Text size="2" color="gray">
                -
              </Text>
            );
          }

          return (
            <DownloadButton
              apiEndpoint={getDownloadEndpoint(fileId, row.original.id)}
              filename={row.original.filename}
              size="small"
              variant="ghost"
              successMessage={`Downloaded ${row.original.filename}`}
              errorMessage="Failed to download file"
            >
              Download
            </DownloadButton>
          );
        },
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: false,
        meta: {
          // minSize: 200,
          maxSize: 'auto',
        },
      },
    ],
    [
      fileId,
      showExtensionColumn,
      getDownloadEndpoint,
      currentVersionId,
      latestImportedVersionId,
      isCsvFileVersion,
      handleVersionStatusChange,
    ]
  );

  // Custom skeleton cell renderer for better UX
  // Currently unused: DataTable provides its own skeletons; keep implementation for potential future tuning.
  const customSkeletonCell = useCallback((columnId: string, _meta?: any) => {
    // Different skeleton sizes based on column type
    switch (columnId) {
      case 'filename':
        return (
          <Flex gap="2" align="center">
            <div
              className="skeleton-box"
              style={{ width: '20px', height: '20px', borderRadius: '4px' }}
            />
            <div
              className="skeleton-box"
              style={{ width: '120px', height: '16px', borderRadius: '4px' }}
            />
          </Flex>
        );
      case 'extension':
        return (
          <div
            className="skeleton-box"
            style={{ width: '60px', height: '20px', borderRadius: '12px' }}
          />
        );
      case 'size':
        return (
          <div
            className="skeleton-box"
            style={{ width: '80px', height: '16px', borderRadius: '4px' }}
          />
        );
      case 'content_type':
        return (
          <div
            className="skeleton-box"
            style={{ width: '100px', height: '16px', borderRadius: '4px' }}
          />
        );
      case 'created_by':
        return (
          <Flex align="center" gap="2">
            <div
              className="skeleton-box"
              style={{ width: '16px', height: '16px', borderRadius: '50%' }}
            />
            <Flex direction="column" gap="1">
              <div
                className="skeleton-box"
                style={{ width: '100px', height: '14px', borderRadius: '4px' }}
              />
              <div
                className="skeleton-box"
                style={{ width: '120px', height: '12px', borderRadius: '4px' }}
              />
            </Flex>
          </Flex>
        );
      case 'created_at':
        return (
          <Flex align="center" gap="1">
            <div
              className="skeleton-box"
              style={{ width: '14px', height: '14px', borderRadius: '4px' }}
            />
            <div
              className="skeleton-box"
              style={{ width: '140px', height: '16px', borderRadius: '4px' }}
            />
          </Flex>
        );
      case 'actions':
        return (
          <div
            className="skeleton-box"
            style={{ width: '80px', height: '32px', borderRadius: '6px' }}
          />
        );
      default:
        return (
          <div
            className="skeleton-box"
            style={{ width: '100px', height: '16px', borderRadius: '4px' }}
          />
        );
    }
  }, []);
  void customSkeletonCell;

  if (!file) return null;

  if (!fileId || fileId <= 0) {
    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Content>
          <Dialog.Title>Error</Dialog.Title>
          <Dialog.Description>Invalid file ID: {fileId}</Dialog.Description>
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        style={{ maxWidth: 1900 }}
        size={modalSize}
        className="file-versions-modal"
      >
        {/* Modal Header */}
        <Dialog.Header
          startSlot={
            <FileFormatIcon
              fileName={file.filename}
              mimeType={file.content_type}
              size="small"
            />
          }
          title={modalTitle}
          subtitle={file.filename}
          endSlot={
            <Button
              variant="ghost"
              size="medium"
              isIconOnly
              onClick={() => onOpenChange(false)}
              startIcon={<IconX />}
              title="Close"
            />
          }
        ></Dialog.Header>

        {/* Content */}
        <Dialog.Body
          style={{ minHeight: 'min(500px, 40vh)', maxHeight: '75vh' }}
        >
          {error && (
            <Alert
              size="small"
              color="error"
              title="Error loading versions"
              description={error?.message || 'An error occurred'}
            />
          )}

          {!error && (
            <DataTable
              scrollMode="table"
              stickyHeader
              data={versionsData || []}
              columns={columns}
              enableSorting={true}
              enableFiltering={false}
              enablePagination={false}
              enableColumnVisibility={true}
              enableRowSelection={false}
              sorting={{
                state: sortingState,
                onSortingChange: setSortingState,
              }}
              loading={isLoading}
              skeletonRows={5}
              // customSkeletonCell={customSkeletonCell}
              emptyMessage="No version history available"
              emptyIcon={<IconClock />}
              customEmptyContent={
                <EmptyContent
                  icon={<IconClock />}
                  title="No version history available"
                  description="This file has no previous versions"
                />
              }
              getRowId={(row: any) =>
                row.id?.toString() || Math.random().toString()
              }
            />
          )}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog.Root>
  );
};
